import type { LocalizedString } from '@/shared/model/Language';
import type { AddonChangeLine } from './addons';

export type AddonPill = {
    name: LocalizedString;
    itemId?: string;
    count: number;
    variantName?: LocalizedString;
    parentName?: LocalizedString;
};

/**
 * The change tree as one flat run of pills: every added choice, nested ones included, then every
 * removal. Nested choices keep the parent line's name so two identical names stay distinct.
 */
export const getAddonPills = (
    nodes: AddonChangeLine[],
    parentName?: LocalizedString,
): { added: AddonPill[]; removed: AddonPill[] } => {
    const added: AddonPill[] = [];
    const removed: AddonPill[] = [];
    for (const node of nodes) {
        if (node.count < 0) {
            removed.push({ name: node.name, itemId: node.itemId, count: Math.abs(node.count), parentName });
            continue;
        }
        const nested = getAddonPills(node.children, node.name);
        const hasNested = nested.added.length + nested.removed.length > 0;
        if (node.count > 0 || node.variantName || hasNested) {
            added.push(
                {
                    name: node.name,
                    itemId: node.itemId,
                    count: node.count,
                    variantName: node.variantName,
                    parentName,
                },
                ...nested.added,
            );
        }
        removed.push(...nested.removed);
    }
    return { added, removed };
};

/** Pill text: `Parent: 2x Name - Variant`, each part only when it is set. */
export const getAddonPillLabel = (
    pill: AddonPill,
    getLocalizedValue: (value: LocalizedString) => string,
): string => {
    const qty = pill.count > 1 ? `${pill.count}x ` : '';
    const variant = pill.variantName ? ` - ${getLocalizedValue(pill.variantName)}` : '';
    const parent = pill.parentName ? `${getLocalizedValue(pill.parentName)}: ` : '';
    return `${parent}${qty}${getLocalizedValue(pill.name)}${variant}`;
};
